import { useContext, useEffect, useState } from "react";
import { useAtom, useAtomValue } from "jotai";

import customAxios from "@/custom/custom-axios";
import { AuthContext } from "@/contexts/auth";
import { GUEST_END_POINTS } from "@/constants/guest/endPoints";
import PhotoRegisterProcess from "@/services/common/photo/register";
import { userDataGlobalAtom } from "@/states/userDataGlobalAtom";
import { contactAtom } from "@/states/common/contactAtom";

import type { UploadFile as UploadFileType } from "@/components/shared/reportDocumentRegisterV2";
import type { UploadPhotos } from "@/components/shared/photoRegisterV2";

export type FileType = "photo" | "file";
type UploadFile = {
  fileType: FileType;
  localUrl: string[];
  url?: string[];
  file: FileList;
};
type Response = {
  id: number;
  url: string;
  token: string;
};

/*
Zendeskへ写真やファイルを送信する
- 対応拡張子（写真）：jpeg,png,jpg,gif,svg
- 対応拡張子（ファイル）：xls,xlsx,doc,docx,pdf
- 最大ファイルサイズ：20MB（1チケットには50MBまで）
*/
export const useZendesk = ({
  photoList,
  fileList
}: {
  photoList?: UploadPhotos[];
  fileList?: UploadFileType[];
}) => {
  const [tokens, setTokens] = useAtom(contactAtom);
  const userData = useAtomValue(userDataGlobalAtom);
  const { token } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any | null>(null);
  const [uploadData, setUploadData] = useState<UploadFile | null>(null);
  const [responseData, setResponseData] = useState<UploadPhotos[] | null>(null);
  const [responseFileData, setResponseFileData] = useState<
    UploadFileType[] | null
  >(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    fileType: FileType;
    token: string;
  } | null>(null);
  const [errorStep, setErrorStep] = useState<string>("");
  const CREW_USER_ALIAS = "Worker";

  // アップ済み写真リストからpathが空のもの（アップエラー）を除外
  const filterPhotoList = () => {
    return photoList?.filter((photo) => photo.path !== "") || [];
  };
  // アップ済みファイルリストからtokenが空のもの（アップエラー）を除外
  const filterFileList = () => {
    return fileList?.filter((f) => f.token !== "") || [];
  };
  // 写真リストからtokenのみのリストを取得
  const filterPhotoListByToken = (newPhotoList: UploadPhotos[]) => {
    return newPhotoList.map((p) => p.token || "") || [];
  };
  // ファイルリストからtokenのみのリストを取得
  const filterFileListByToken = (newFileList: UploadFileType[]) => {
    return newFileList.map((f) => f.token || "") || [];
  };
  // 写真リストとトークンリストの更新
  const upadatePhotoListAndTokens = (newPhotoList: UploadPhotos[]) => {
    setResponseData(newPhotoList);
    setTokens({
      ...tokens,
      photoTokens: filterPhotoListByToken(newPhotoList)
    });
  };
  // ファイルリストとトークンリストの更新
  const upadateFileListAndTokens = (newFileList: UploadFileType[]) => {
    setResponseFileData(newFileList);
    setTokens({
      ...tokens,
      fileTokens: filterFileListByToken(newFileList)
    });
  };

  //Zendeskにファイルをアップロード
  const uploadFile = async (file: FileList, fileType: FileType) => {
    const userType = userData?.user;
    const basePath =
      userType === CREW_USER_ALIAS
        ? GUEST_END_POINTS.CONTACT.FILE_UPLOAD.CREW
        : GUEST_END_POINTS.CONTACT.FILE_UPLOAD.OWNER;
    const apiUrl = file.length === 1 ? basePath.SINGLE : basePath.MULTIPLE;

    const photoSave = new PhotoRegisterProcess();
    const payload = new FormData();

    // ファイル加工処理（写真の場合）
    const processFile = async (f: File, index: number | null = null) => {
      const procFile =
        fileType === "photo" ? await photoSave.analyzePhoto(f) : { file: f };
      const name = index !== null ? `files[${index}]` : "file";
      payload.append(name, procFile.file);
    };

    //写真の場合、圧縮等の加工を行う
    setErrorStep("processFile");
    if (file.length === 1) {
      await processFile(file[0]);
    } else {
      await Promise.all([...file].map((f, index) => processFile(f, index)));
    }

    //zendeskへアップロード
    setErrorStep(`upload ${fileType} to zendesk`);
    const res = await customAxios.post(apiUrl, payload, token);
    const responses = file.length === 1 ? [res.data] : res.data;

    //アップロード済みリストの更新
    if (fileType === "photo") {
      updatePhotoList(responses);
    } else {
      updateFileList(responses);
    }
  };

  //アップロード済み写真リストの更新
  const updatePhotoList = (responses: Response[]) => {
    responses.forEach((response) => {
      const existingPhoto = photoList?.find(
        (photo) => photo.id === response.id
      );
      if (!existingPhoto) {
        photoList?.push({
          id: response.id,
          path: response.url,
          previewUrl: response.url,
          token: response.token,
          is_aspect_ratio_valid: true, //不要だが型エラー回避のため
          captured_at: "" //不要だが型エラー回避のため
        });
      }
    });

    const newPhotoList = filterPhotoList();
    upadatePhotoListAndTokens(newPhotoList);
  };

  //アップロード済みファイルリストの更新
  const updateFileList = (responses: Response[]) => {
    responses.forEach((response) => {
      const existingFile = fileList?.find((f) => f.id === response.id);
      const urlObj = new URL(response.url);
      const nameParam = urlObj.searchParams.get("name");

      if (!existingFile) {
        fileList?.push({
          id: response.id,
          token: response.token,
          url: response.url,
          fileName: nameParam || "",
          extension: nameParam?.split(".").pop() || ""
        });
      }
    });

    const newFileList = filterFileList();
    upadateFileListAndTokens(newFileList);
  };

  //ファイルアップロード
  useEffect(() => {
    (async () => {
      if (uploadData?.file && uploadData?.file.length > 0) {
        try {
          setIsLoading(true);
          await uploadFile(uploadData.file, uploadData.fileType);
        } catch (error) {
          setError(error);

          //アップロード失敗時の処理（エラーになったものをアップ済リストから削除 & 保管しているトークンからも削除）
          console.error("zendesk file upload error", error);
          if (uploadData.fileType === "photo") {
            const newPhotoList = filterPhotoList();
            upadatePhotoListAndTokens(newPhotoList);
          } else {
            const newFileList = filterFileList();
            upadateFileListAndTokens(newFileList);
          }
        } finally {
          setIsLoading(false);
          setUploadData(null);
        }
      }
    })();
  }, [uploadData]);

  //ファイル削除
  useEffect(() => {
    (async () => {
      if (deleteTarget) {
        const userType = userData?.user;
        const apiPath =
          userType === CREW_USER_ALIAS
            ? GUEST_END_POINTS.CONTACT.FILE_UPLOAD.CREW.DELETE
            : GUEST_END_POINTS.CONTACT.FILE_UPLOAD.OWNER.DELETE;
        const isPhoto = deleteTarget.fileType === "photo";

        try {
          setIsLoading(true);
          setErrorStep("delete file from zendesk");
          await customAxios.delete(`${apiPath}/${deleteTarget.token}`, token);
          setDeleteTarget(null);

          //削除後の処理（アップ済みファイルリストの更新）
          if (isPhoto) {
            const newPhotoList =
              photoList?.filter(
                (photo) => photo.token !== deleteTarget.token
              ) || [];
            upadatePhotoListAndTokens(newPhotoList);
          } else {
            const newFileList =
              fileList?.filter((f) => f.token !== deleteTarget.token) || [];
            upadateFileListAndTokens(newFileList);
          }
        } catch (error) {
          setError(error);
          console.error("zendesk file delete error", error);
          //削除失敗時の処理（エラーになったものをアップ済リストから削除 & 保管しているトークンからも削除）
          if (isPhoto) {
            const newPhotoList = filterPhotoList();
            upadatePhotoListAndTokens(newPhotoList);
          } else {
            const newFileList = filterFileList();
            upadateFileListAndTokens(newFileList);
          }
        } finally {
          setIsLoading(false);
        }
      }
    })();
  }, [deleteTarget]);

  return {
    zendeskUploadData: uploadData,
    setZendeskUploadData: setUploadData,
    zendeskUploadResponse: responseData,
    zendeskUploadFileResponse: responseFileData,
    setZendeskDeleteTarget: setDeleteTarget,
    setZendeskUploadError: setError,
    zendeskUploadError: error,
    zendeskUploadIsLoading: isLoading,
    errorStep
  };
};

/*
MEMO: Zendeskへの写真添付は以下のイメージ
写真やPdfをゼンデスクにアップロード
↓
それを問い合わせに紐付ける
*/
/*
MEMO: 写真アップロードの仕様
アップした写真を消した場合、どれが残っているのか不明なため、フロントでリスト管理（IndexedDBを使うか迷ったが、少ないのでSessionStorageで）
*/
